/**
 * Cliente API simple. El Vite proxy reenvía /api al backend Next.js (3000).
 *
 * AUTO-LOGOUT: si cualquier request retorna 401, el cliente limpia el
 * zustand persistido (localStorage key "maznet-admin-auth") y redirige a
 * /login. Esto evita el caso "sesión huérfana" en que el zustand muestra
 * `user` pero la cookie JWT está vencida/invalida (típico tras un
 * deployment del backend que cambia el secret).
 */

function clearSessionAndRedirect(): void {
  try {
    localStorage.removeItem('maznet-admin-auth')
  } catch {
    /* noop */
  }
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login'
  }
}

async function parseOrThrow<T>(r: Response, path: string): Promise<T> {
  if (r.status === 401) {
    clearSessionAndRedirect()
    throw new Error('Sesión expirada — vuelve a iniciar sesión')
  }
  const data = await r.json().catch(() => ({}))
  if (!r.ok) {
    throw new Error((data as { error?: string })?.error || `HTTP ${r.status} en ${path}`)
  }
  return data as T
}

export async function apiGet<T>(path: string): Promise<T> {
  const r = await fetch(path, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
  if (r.status === 401) {
    clearSessionAndRedirect()
    throw new Error('Sesión expirada — vuelve a iniciar sesión')
  }
  if (!r.ok) throw new Error(`HTTP ${r.status} en ${path}`)
  return r.json()
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const r = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  return parseOrThrow<T>(r, path)
}

export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  const r = await fetch(path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  return parseOrThrow<T>(r, path)
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  const r = await fetch(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  return parseOrThrow<T>(r, path)
}

export async function apiDelete<T>(path: string): Promise<T> {
  const r = await fetch(path, {
    method: 'DELETE',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
  return parseOrThrow<T>(r, path)
}

export function formatMXN(n: number): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
  }).format(n)
}

export function slugify(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-')
}
